import { type ReactNode, useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FocusTrap } from 'focus-trap-react'
import cx from 'classnames'

import { type UpdateUrlParameters } from '@lib/hooks'

import RadioGroup from '@components/radio-group'
import RadioItem from '@components/radio-item'

interface ListOption {
  type: string
  title: string
}

interface CollectionListboxProps {
  id: string
  name: string
  label: string
  options: ListOption[]
  activeOption: string | null
  before?: ReactNode
  after?: ReactNode
  updateParameters: UpdateUrlParameters
  className?: string
}

const CollectionListbox = ({
  id,
  name,
  label,
  options,
  activeOption,
  before,
  after,
  updateParameters,
  className,
}: CollectionListboxProps) => {
  const groupRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)

  const currentOption =
    options.find((option) => option.type === activeOption) ?? options[0]

  const handleChange = (value: string) => {
    updateParameters([
      {
        name,
        value,
      },
    ])
    setIsOpen(false)
  }

  function onOutsideClick(event: MouseEvent) {
    if (event.target && groupRef.current?.contains(event.target as Element)) {
      return
    }

    setIsOpen(false)
  }

  function handleKeyDown(event: KeyboardEvent) {
    let flag = false

    switch (event.code) {
      case 'Escape': {
        setIsOpen(false)
        flag = true
        break
      }

      default: {
        break
      }
    }

    if (flag) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', onOutsideClick)

    return () => {
      document.removeEventListener('mousedown', onOutsideClick)
    }
  }, [])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  return (
    <div ref={groupRef} className={cx('flex relative z-20', className)}>
      <span id={`${id}-label`} className="sr-only">
        {label}
      </span>

      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-labelledby={`${id}-label`}
        className="text-xs uppercase leading-none"
      >
        {before}
        {currentOption?.title}
        {after}
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <FocusTrap
            focusTrapOptions={{
              clickOutsideDeactivates: true,
              preventScroll: true,
            }}
          >
            <div className="absolute top-[calc(100%+14px)] overflow-hidden right-0">
              <motion.div
                initial="hide"
                animate="show"
                exit="hide"
                variants={{
                  show: {
                    y: '0%',
                  },
                  hide: {
                    y: '-100%',
                  },
                }}
                transition={{
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="bg-pageBG overflow-hidden whitespace-nowrap"
              >
                <RadioGroup
                  value={currentOption.type}
                  onChange={(value) => handleChange(value)}
                  role="listbox"
                  className="flex flex-col p-2 border border-gray"
                >
                  {options.map((option, index) => (
                    <RadioItem
                      key={index}
                      value={option.type}
                      role="option"
                      aria-selected={
                        option.type === currentOption.type ? 'true' : 'false'
                      }
                      className={cx('text-xs uppercase text-left p-1')}
                    >
                      {option.title}
                    </RadioItem>
                  ))}
                </RadioGroup>
              </motion.div>
            </div>
          </FocusTrap>
        )}
      </AnimatePresence>
    </div>
  )
}

export default CollectionListbox
